import React, { useCallback, useMemo, useRef, useState, useContext, useEffect } from "react";
import classNames from "classnames";
import GroupContext from "../../context/group";

const FormSelect = ({ id, field, label, options, isRequired = false, className, ...other }) => {
  const { formData, onChange, formErrors } = useContext(GroupContext);

  const selectRef = useRef();
  const toggleRef = useRef();
  const optionsRef = useRef({});
  const setOptionRef = useCallback((el) => {
    if (el) {
      optionsRef.current[el.name] = el;
    }
  }, []);
  const customSelectRef = useRef();
  const [open, setOpen] = useState(false);

  const onFocus = useCallback(() => 0, []);
  const onBlur = useCallback(() => 0, []);
  const allOptions = useMemo(
    () => options.map((option) => (option.group ? option.group : [option])).flat(),
    [options]
  );
  const currentOption = allOptions.find((item) => item.value === formData[field]);
  const currentValue = currentOption ? currentOption.value : null;

  const [focusedOption, setFocusedOption] = useState();
  const focusOption = useCallback((e) => setFocusedOption(e.currentTarget.name), []);
  const blurOption = useCallback(
    (e) => {
      const relatedName = e.relatedTarget?.name;
      setFocusedOption((value) =>
        allOptions.some((item) => item.value === relatedName) ? value : ""
      );
    },
    [allOptions]
  );

  useEffect(() => {
    if (open && !focusedOption) {
      setOpen(false);
    } else if (!open && focusedOption) {
      setOpen(true);
    }
  }, [open, focusedOption]);

  const [focusing, setFocusing] = useState(false);
  const selectClick = (e) => {
    // console.log('selectClick: ', focusing)
    if (focusing) {
      setFocusing(false);
    } else {
      // console.log('focus for safari')
      e.currentTarget.focus();
    }
  };

  const selectOption = useCallback((e) => {
    e.preventDefault();
    if (selectRef.current) {
      selectRef.current.value = e.currentTarget.name;
      selectRef.current.dispatchEvent(new Event("change", { bubbles: true }));
      e.currentTarget.blur();
    }
  }, []);

  const focusSelect = useCallback(
    (e) => {
      // console.log('focusSelect')
      // console.log('e.relatedTarget: ', e.relatedTarget?.name)
      setFocusing(true);
      if (allOptions.some((item) => item.value === e.relatedTarget?.name)) {
        // console.log('blur to close');
        e.currentTarget.blur();
      } else {
        // console.log('Focus on selection: ', optionsRef.current);
        const focusBtn = currentValue
          ? optionsRef.current[currentValue]
          : optionsRef.current[allOptions[1].value];
        if (focusBtn) {
          focusBtn.focus();
        }
      }
    },
    [currentValue, allOptions]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        const currentItemIdx = allOptions.findIndex((item) => item.value === focusedOption);
        const nextItem = currentItemIdx ? allOptions[currentItemIdx + 1] : null;
        const nextName = nextItem?.value;
        if (nextName) {
          const focusBtn = optionsRef.current[nextName];
          if (focusBtn) {
            focusBtn.focus();
            e.preventDefault();
          }
        }
      }
      if (e.key === "ArrowUp") {
        const currentItemIdx = allOptions.findIndex((item) => item.value === focusedOption);
        const prevItem = currentItemIdx ? allOptions[currentItemIdx - 1] : null;
        const prevName = prevItem?.value;
        if (prevName) {
          const focusBtn = optionsRef.current[prevName];
          if (focusBtn) {
            focusBtn.focus();
            e.preventDefault();
          }
        }
      }
    },
    [focusedOption, allOptions]
  );

  return (
    <div className={`form__select${className ? ` ${className}` : ""}`} {...other}>
      {label && (
        <label
          className={classNames("form__label", { "form__label--required": isRequired })}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <div className={classNames("form__select-wrapper", { "form__select-wrapper--open": open })}>
        <button
          type="button"
          ref={toggleRef}
          onFocus={focusSelect}
          onClick={selectClick}
          name={label}
          className={classNames("form__select-value", {
            "form__select-value--default": !currentValue,
          })}
        >
          {currentOption ? currentOption.label : ""}
        </button>
        <select
          ref={selectRef}
          id={id}
          value={currentValue}
          name={field}
          onFocus={onFocus}
          onChange={onChange}
          onBlur={onBlur}
          tabIndex="-1"
          data-required={isRequired}
        >
          {options.map((item) => (
            <React.Fragment
              key={item.group ? item.group.map((opt) => opt.value).join("-") : item.value}
            >
              {item.group ? (
                <optgroup label={item.label}>
                  {item.group.map((opt) => (
                    <option value={opt.value} key={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ) : (
                <option value={item.value}>{item.label}</option>
              )}
            </React.Fragment>
          ))}
        </select>
        <div
          ref={customSelectRef}
          className={classNames("form__select-dropdown", {
            "form__select-dropdown--open": open,
          })}
        >
          {options
            .filter((opt) => opt.group || opt.value)
            .map((item) => (
              <React.Fragment
                key={item.group ? item.group.map((opt) => opt.value).join("-") : item.value}
              >
                {item.group ? (
                  <>
                    <div className="form__select-dropdown-group">{item.label}</div>
                    {item.group.map((opt) => (
                      <button
                        ref={setOptionRef}
                        className="form__select-dropdown-option"
                        name={opt.value}
                        key={opt.value}
                        onClick={selectOption}
                        onFocus={focusOption}
                        onBlur={blurOption}
                        onKeyDown={onKeyDown}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </>
                ) : (
                  <button
                    ref={setOptionRef}
                    className="form__select-dropdown-option"
                    name={item.value}
                    onClick={selectOption}
                    onFocus={focusOption}
                    onBlur={blurOption}
                    onKeyDown={onKeyDown}
                  >
                    {item.label}
                  </button>
                )}
              </React.Fragment>
            ))}
        </div>
        {formErrors[field] && (
          <span className="form__error-message d-inline">{formErrors[field]}</span>
        )}
      </div>
    </div>
  );
};

export default FormSelect;
